import type {
  CalendarEventsRecord,
  ExerciseLogsRecord,
  GradesRecord,
  HomeworkRecord,
} from '@/types/collections';
import { createRepository } from '@/services/repository';

class HomeReadRepository {
  private eventsRepo = createRepository<CalendarEventsRecord>('calendar_events');
  private homeworkRepo = createRepository<HomeworkRecord>('homework');
  private exerciseRepo = createRepository<ExerciseLogsRecord>('exercise_logs');
  private gradesRepo = createRepository<GradesRecord>('grades');
  private resolvedEventSort: 'start_date' | 'startDate' | '-created' | null = null;
  private resolvingEventSort: Promise<void> | null = null;

  setUserId(userId: string): void {
    this.eventsRepo.setUserId(userId);
    this.homeworkRepo.setUserId(userId);
    this.exerciseRepo.setUserId(userId);
    this.gradesRepo.setUserId(userId);
  }

  private isEventSortIssue(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return (
      message.includes('invalid sort field') ||
      message.includes('invalid filter') ||
      message.includes('Failed to load resource') ||
      message.includes('Something went wrong while processing your request')
    );
  }

  private async resolveEventSort(): Promise<void> {
    if (this.resolvedEventSort) return;
    if (this.resolvingEventSort) return this.resolvingEventSort;

    this.resolvingEventSort = (async () => {
      const attempts: Array<'start_date' | 'startDate' | '-created'> = ['start_date', 'startDate', '-created'];
      let lastError: unknown = null;

      for (const sort of attempts) {
        try {
          await this.eventsRepo.list({ sort, page: 1, perPage: 1, skipTotal: true });
          this.resolvedEventSort = sort;
          return;
        } catch (error) {
          lastError = error;
          if (!this.isEventSortIssue(error)) {
            throw error;
          }
        }
      }

      throw lastError instanceof Error ? lastError : new Error('Failed to resolve event sort strategy');
    })().finally(() => {
      this.resolvingEventSort = null;
    });

    return this.resolvingEventSort;
  }

  async listUpcomingEvents() {
    await this.resolveEventSort();

    try {
      return await this.eventsRepo.list({ sort: this.resolvedEventSort || '-created' });
    } catch (error) {
      if (!this.isEventSortIssue(error)) {
        throw error;
      }

      this.resolvedEventSort = null;
      await this.resolveEventSort();
      return this.eventsRepo.list({ sort: this.resolvedEventSort || '-created' });
    }
  }

  async listPendingHomework() {
    return this.homeworkRepo.list({
      filter: 'completed = false',
      sort: '-due_date',
    });
  }

  async listRecentWorkouts(days: number) {
    const dateFrom = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
    return this.exerciseRepo.list({
      filter: `date >= "${dateFrom}"`,
      sort: '-date',
    });
  }

  async listRecentGrades() {
    return this.gradesRepo.list({
      sort: '-date',
      page: 1,
      perPage: 10,
    });
  }
}

export const homeReadRepository = new HomeReadRepository();
